import {
  Icon,
  TableCell,
  TableRow,
  Tooltip,
  Pill,
  Typography
} from '@sanlam-glacier/app-common/components';
import { ProposalOverviewViewModel } from 'bff';
import { format } from 'date-fns';
import { useNavigate } from 'react-router';
import { generateEditProposalPath } from 'routes/route-paths';
import { environmentService } from 'stores/store';
import style from './proposal-row.module.scss';
import { OverviewContextMenu } from '../overview-context-menu/OverviewContextMenu';
import { Show } from '@sanlam-glacier/app-common/components';
import { truncateClientName } from 'modules/new-business/utils/proposal/truncate-client-name.function';

type ProposalOverviewTableProps = {
  row: ProposalOverviewViewModel;
  disabled: boolean;
};

const baseTypographyProps = {
  align: Typography.ALIGN.LEFT,
  variant: Typography.VARIANT.B4,
  weight: Typography.WEIGHT.NORMAL,
  className: style.textCell
};

const renderProductList = (row: ProposalOverviewViewModel) => {
  return (
    <ul>
      {row.productNames?.map((name) => (
        <li className={style.list} key={name}>
          {name}
        </li>
      ))}
    </ul>
  );
};

export const ProposalTableRow = ({ row, disabled }: ProposalOverviewTableProps) => {
  const navigate = useNavigate();
  const goToProposal = () => {
    navigate(generateEditProposalPath(row.id));
  };

  return (
    <TableRow disabled={disabled} key={row.id} hoverable={!disabled} onClick={() => goToProposal()}>
      <TableCell
        data-label='Lock status'
        align={TableCell.ALIGN.LEFT}
        padding={TableCell.PADDING.CHECKBOX}
      >
        <Icon
          icon={row.locked ? 'action_lock' : 'action_lock-open-solid'}
          className={`${style.proposalIcon} ${row.locked ? '' : style.unlockedProposalIcon}`}
        />
      </TableCell>
      <TableCell data-label='Proposal #'>
        <Typography
          {...baseTypographyProps}
          color={Typography.COLOR.PRIMARY}
          weight={Typography.WEIGHT.MEDIUM}
        >
          {row.id}
        </Typography>
      </TableCell>
      <TableCell data-label='Created'>
        <Typography {...baseTypographyProps}>
          {format(row.dateCreated, 'yyyy - MM - dd')}
        </Typography>
      </TableCell>
      <TableCell data-label='Client name'>
          <Typography {...baseTypographyProps}>{truncateClientName(row.clientName)}</Typography>
      </TableCell>
      <TableCell data-label='Product name'>
        <Typography {...baseTypographyProps} className={style.textCellWrap}>
          <Tooltip
            content={renderProductList(row)}
            headline={'Products'}
            position={Tooltip.POSITION.LEFT}
          >
            <span>{row.productName}</span>
          </Tooltip>
        </Typography>
      </TableCell>
      <TableCell data-label='Proposal value'>
        <Typography {...baseTypographyProps} align={Typography.ALIGN.RIGHT}>
          {row.proposalLumpSumValue?.ToCurrencyString({ excludePrefix: true })}
        </Typography>
        <Show when={row.proposalRecurringValue !== undefined}>
          <Typography {...baseTypographyProps} align={Typography.ALIGN.RIGHT}>
            {`[${row.proposalRecurringValue?.ToCurrencyString({ excludePrefix: true })} recur]`}
          </Typography>
        </Show>
      </TableCell>
      <TableCell data-label='Currency'>
        <Typography {...baseTypographyProps}>{environmentService.getCurrencyCode()}</Typography>
      </TableCell>
      <TableCell data-label='Status' align={TableCell.ALIGN.RIGHT} className={style.tablePillCell}>
        <Pill variant={Pill.VARIANT.SECONDARY} disabled={row.locked}>
          <Typography {...baseTypographyProps} color={Typography.COLOR.INHERIT}>
            {row.statusId}
          </Typography>
        </Pill>
      </TableCell>
      <TableCell data-label='Last modified'>
        <Typography {...baseTypographyProps}>
          {row.lastOpened ? format(row.lastOpened, 'yyyy - MM - dd') : '-'}
        </Typography>
      </TableCell>
      <TableCell data-label='Action'>
        <OverviewContextMenu
          proposalId={row.id}
          isLocked={row.locked ?? false}
          isDisabled={disabled}
        />
      </TableCell>
    </TableRow>
  );
};
